import { EthereumProvider } from "@walletconnect/ethereum-provider";
import { WalletConnectModal } from "@walletconnect/modal";
import { getNumericChainId } from "../model/NetworksDetail";
import walletConnectStorage from "./walletConnectStorage";

let walletConnectProviderInstance = null;
let inFlightInitPromise = null;
let inFlightConnectPromise = null;
let walletConnectModal = null;

/**
 * Gets or initializes the WalletConnect v2 EthereumProvider singleton for KUB Wallet.
 * Reuses a restored session when valid and unexpired; otherwise initiates connect().
 * In-flight promise guards against duplicate modals or concurrent connection attempts.
 * @param {object} chainDetail 
 * @returns {Promise<any>}
 */
export async function getWalletConnectProvider(chainDetail) {
    const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;
    if (!projectId) {
        const error = new Error("REACT_APP_WALLETCONNECT_PROJECT_ID is not configured");
        error.code = "MISSING_PROJECT_ID";
        throw error;
    }

    const numericChainId = getNumericChainId(chainDetail);
    const rpcUrl = (chainDetail && chainDetail.rpcUrls && chainDetail.rpcUrls[0]) || "https://rpc.kubchain.io";

    if (!walletConnectProviderInstance) {
        if (!inFlightInitPromise) {
            inFlightInitPromise = EthereumProvider.init({
                // Shared across concurrent Unity connection requests.
                projectId,
                storage: walletConnectStorage,
                chains: [numericChainId],
                optionalChains: [numericChainId],
                rpcMap: {
                    [numericChainId]: rpcUrl,
                },
                // The provider aborts pairing whenever its own modal closes,
                // including a close during wallet handoff before session approval.
                showQrModal: false,
                metadata: {
                    name: "Morning Moon Village",
                    description: "Morning Moon Village",
                    url: typeof window !== "undefined" ? window.location.origin : "https://morningmoonvillage.com",
                    icons: [
                        typeof window !== "undefined"
                            ? `${window.location.origin}/assets/images/game/morningmoon-logo.png`
                            : "https://morningmoonvillage.com/assets/images/game/morningmoon-logo.png",
                    ],
                },
            }).then((provider) => {
                // Ensure legacy Web3.js compatibility when Web3 expects send(payload, callback).
                if (!provider.send && typeof provider.sendAsync === "function") {
                    provider.send = provider.sendAsync.bind(provider);
                }
                walletConnectProviderInstance = provider;
                return provider;
            }).finally(() => {
                inFlightInitPromise = null;
            });
        }
        await inFlightInitPromise;
    }

    const hasValidSession = Boolean(
        walletConnectProviderInstance.session &&
        walletConnectProviderInstance.session.expiry &&
        walletConnectProviderInstance.session.expiry * 1000 > Date.now()
    );

    if (!hasValidSession) {
        if (!inFlightConnectPromise) {
            const provider = walletConnectProviderInstance;
            inFlightConnectPromise = (async () => {
                // Clear an expired persisted session before starting a new pairing.
                if (provider.session) await provider.disconnect();
                if (!walletConnectModal) {
                    walletConnectModal = new WalletConnectModal({ projectId });
                }
                const onDisplayUri = (uri) => {
                    walletConnectModal.openModal({ uri }).catch((error) => {
                        console.error("[WalletConnect] Could not open QR modal:", error);
                    });
                };
                provider.on("display_uri", onDisplayUri);
                try {
                    await provider.connect();
                } finally {
                    provider.removeListener("display_uri", onDisplayUri);
                    walletConnectModal.closeModal();
                }
            })()
                .finally(() => {
                    inFlightConnectPromise = null;
                });
        }
        await inFlightConnectPromise;
    }

    return walletConnectProviderInstance;
}

/**
 * Checks whether an active and unexpired WalletConnect session exists.
 * @returns {boolean}
 */
export function hasActiveWalletConnectSession() {
    return Boolean(
        walletConnectProviderInstance &&
        walletConnectProviderInstance.session &&
        walletConnectProviderInstance.session.expiry &&
        walletConnectProviderInstance.session.expiry * 1000 > Date.now()
    );
}

/**
 * Explicitly terminates the WalletConnect session and clears internal provider state.
 * @returns {Promise<void>}
 */
export async function disconnectWalletConnect() {
    if (walletConnectProviderInstance) {
        if (walletConnectProviderInstance.session) {
            try {
                await walletConnectProviderInstance.disconnect();
            } catch (err) {
                console.warn("[WalletConnect] disconnect error:", err);
            }
        }
        walletConnectProviderInstance = null;
        inFlightConnectPromise = null;
    }
}
